/* eslint-disable max-lines */
import * as React from "react";

import { connect, useSelector } from "react-redux";
import { Header, Loader, Button, Container, Segment, Divider } from "semantic-ui-react";
import { createValidator, validators } from "not-valid";
import { uniq, flatten, values } from "lodash";
import isPostcodeValid from "uk-postcode-validator";

import {
    AnsweredQuestion, AnsweredQuestionWithId, QuestionnairesState, FileAnswer, OrgLevel, QuestionnaireTypes
} from "@teamprevent/common/questionnaires/model";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";

import {
    updateQuestion,
    openSection,
    clearQuestionnaire,
    loadQuestionnaire as loadQuestionnaireAction,
    removeQuestion,
    submitPhysiotherapyReferralQuestionnaire
} from "@teamprevent/common/questionnaires/actions";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/physiotherapyMgrReferral/questionIds";
import { ClassBasedProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/ClassBasedProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { CUSTOMER_ID } from "@teamprevent/common/questionnaires/routerParams";
import { isCustomerUser } from "@teamprevent/common/auth/roles";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import { ReferralFormState } from "@teamprevent/common/questionnaires/components/referralForm/models";
import { FileQuestion } from "@teamprevent/common/questionnaires/components/questions/FileQuestion";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import {
    CURRENT_WORK_STATUS,
    BODY_PARTS,
    PhysiotherapyReferralFormProps,
    PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY,
    attachmentsSectionId,
    orgLevelQuestionId,
    furtherDetailsSectionId,
    furtherDetailsSectionTitle,
    lineManagerHrSectionId,
    employeeDetailSectionId,
    reasonsForReferralSectionId
} from "../models";
import { usePrevious } from "../../customHooks";
import { purchaseOrderNumberValidator } from "../../ohCase/validators/purchaseOrderNumberValidator";

import { Location } from "redux-little-router";

interface StateProps {
    currentUser: { name: string; id: string; email: string; mobilePhone: string; roles: string[] };
    formQuestions: AnsweredQuestionWithId[];
    orgLevels: OrgLevel[];
    ohCaseId: string;
    customerId: string;
    questionnaireId: string;
}

interface DispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion; title?: string }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
    openFirstSection: () => void;
    clearQuestionnaire: () => void;
    loadQuestionnaire: (apiUrl: string, customerId: string, questionnaireId: string, caseId: string) => void;
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], linkedOhCaseId: string) => Promise<string>;
}

type AllProps = PhysiotherapyReferralFormProps & StateProps & DispatchProps;

const PhysiotherapyMgrReferralUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const currentWorkStatus = { id: questionIds.CurrentWorkStatus, notAtWorkAnswer: "Not at work" };

    const [selectedOrgStructureLevelIds, setSelectedOrgStructureLevelIds] = React.useState<string[]>([]);
    const [documentCount, setDocumentCount] = React.useState<number>(0);

    const linkedOhCaseId = useSelector((state: { router: Location }) => state.router.params?.linkedOhCaseId);
    const formQuestions: AnsweredQuestionWithId[] = useSelector((state: { questionnaires: QuestionnairesState }) =>
        state.questionnaires[PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY]
            ? uniq(flatten(state.questionnaires[PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY].sections.map(section => values(section.questions))))
            : []
    );
    const previousQuestions: AnsweredQuestionWithId[] = usePrevious(formQuestions) ?? [];
    const api = props.api || "/api/physiotherapy";
    const canSubmitFormWhenOrgLevelsAreDisabled = getFeatureToggles()[FeatureToggleNames.CanSubmitFormWhenOrgLevelsAreDisabled];

    const { updateAnswer } = props;

    React.useEffect(() => {
        props.openFirstSection();
        if (!props.readonly) {
            props.loadOrgStructure("");
        }

        return props.clearQuestionnaire;
    }, []);

    React.useEffect(() => {
        if (props.readonly) {
            return;
        }

        const updateOrgLevel = (level: number, questionId: string) => {
            const oldOrgLevel = (previousQuestions.find(question => question.id === questionId) &&
                previousQuestions.find(question => question.id === questionId)?.answer);
            const newOrgLevel = getAnswerToQuestionWithId(questionId);
            if (newOrgLevel && oldOrgLevel !== newOrgLevel) {
                changeOrgStructureLevel(level, newOrgLevel as string);
            }
        };
        updateOrgLevel(0, questionIds.OrgLevel1Id);
        updateOrgLevel(1, questionIds.OrgLevel2Id);
        updateOrgLevel(2, questionIds.OrgLevel3Id);
        updateOrgLevel(3, questionIds.OrgLevel4Id);
    });

    const loadQuestionnaire = (): void => {
        if (!linkedOhCaseId && (props.customerId && props.questionnaireId || props.ohCaseId)) {
            props.loadQuestionnaire(api, props.customerId, props.questionnaireId, props.ohCaseId);
        }
    };

    const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

        const stateQuestion = props.formQuestions.find(q => q.id === questionId);

        if (stateQuestion) {
            props.updateAnswer({
                sectionId: employeeDetailSectionId,
                id: stateQuestion.id,
                question: { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() }
            });
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    };

    const submitQuestionnaire = async (questions: AnsweredQuestionWithId[]): Promise<boolean> => {

        trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredTitle, questions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredFirstName, questions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredLastName, questions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.EmailAddress, questions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.ConfirmEmailAddress, questions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.JobTitle, questions);

        const questionnaireValid = props.formQuestions.every(question => question.isValid);
        const ohCaseId = await props.submitQuestionnaire(api, questions, linkedOhCaseId);
        if (questionnaireValid) {
            props.afterSubmit(ohCaseId);
            return true;
        } else {
            return false;
        }
    };

    const getQuestionConfig = (questionId: string) => {
        return props.questionnaireConfig.questions.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (id: string) => {
        return props.formQuestions.find(question => question.id === id) && props.formQuestions.find(question => question.id === id).answer;
    };

    const addDocument = React.useCallback(() => {
        updateAnswer({
            sectionId: attachmentsSectionId,
            id: `Document${documentCount + 1}`,
            question: { question: "", answer: { blobId: "", fileName: "" } }
        });
        const currentDocumentCount = documentCount;
        setDocumentCount(currentDocumentCount + 1);
    }, [updateAnswer, documentCount]);

    const changeOrgStructureLevel = (level: number, selectedId: string) => {
        setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, level), selectedId]);
        const selectedOrgLevel = props.orgLevels.find(orgLevel => orgLevel.id === selectedId);
        if (!selectedOrgLevel) {
            return;
        }

        const selectedName = selectedOrgLevel.name;
        const thisOrgLevelNameQuestion = props.formQuestions.find(question => question.id === `${orgLevelQuestionId}${level + 1}Name`);

        if (!canSubmitFormWhenOrgLevelsAreDisabled || thisOrgLevelNameQuestion) {
            props.updateAnswer({
                sectionId: furtherDetailsSectionId,
                id: thisOrgLevelNameQuestion.id,
                question: { ...thisOrgLevelNameQuestion, answer: selectedName },
                title: furtherDetailsSectionTitle
            });
        }

        for (let i = level + 2; i <= 4; i++) {
            const orgLevelIdQuestion = props.formQuestions.find(question => question.id === `${orgLevelQuestionId}${i}Id`);

            if (!canSubmitFormWhenOrgLevelsAreDisabled || orgLevelIdQuestion) {
                props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelIdQuestion.id,
                    question: { ...orgLevelIdQuestion, answer: "" },
                    title: furtherDetailsSectionTitle
                });
            }

            const orgLevelNameQuestion = props.formQuestions.find(question => question.id === `${orgLevelQuestionId}${i}Name`);

            if (!canSubmitFormWhenOrgLevelsAreDisabled || orgLevelNameQuestion) {
                props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelNameQuestion.id,
                    question: { ...orgLevelNameQuestion, answer: "" },
                    title: furtherDetailsSectionTitle
                });
            }
        }
        props.loadOrgStructure(selectedId);
    };

    const getQuestionMandatoryConfig = (questionId: string, defaultValue: boolean = true) => {
        const questionConfig = props.questionnaireConfig.questions.find(c => c.id === questionId);

        if (questionConfig)
        {
            const customConfig = (questionConfig as CustomQuestionConfig);

            if (customConfig.isMandatory !== null && customConfig.isMandatory !== undefined) {
                return customConfig.isMandatory;
            }
        }

        return defaultValue;
    };

    return (
        <Container className="physiotherapy-form">
            {props.readonly ?
                <Container>
                    <Segment basic>
                        <Header as="h1">Referral</Header>
                    </Segment>
                </Container>
                :
                <Container>
                    <Segment basic>
                        <Header as="h1">Physiotherapy Referral</Header>
                    </Segment>
                </Container>
            }
            {/* we need to use OldProgressBarQuestionnaireContainer here for now until we can fix the PhysiotherapyMgrReferralForm component in admin app which
            sets a load of questions before the questionnaire has finished loading and renders for the first time and then breaks
            (the questionnaire renders slightly earlier on the old component so this is not an issue) */}
            {props.currentUser.id || props.readonly ?
                <ClassBasedProgressBarQuestionnaireContainer
                    loadQuestionnaire={loadQuestionnaire}
                    submitQuestionnaire={submitQuestionnaire}
                    readonly={props.readonly}
                    questionnaireKey={PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY}
                    questionnaireType={QuestionnaireTypes.Other}
                >
                    <OldProgressBarQuestionnaireSection title="Data Privacy" id="DataPrivacy">
                        <p>
                            Please tick all that apply
                        </p>
                        <CheckboxQuestion
                            questionConfig={getQuestionConfig(questionIds.PrivacyConsent)}
                            readonly={props.readonly}
                            value="Granted"
                        />
                        <CheckboxQuestion
                            questionConfig={getQuestionConfig(questionIds.EmployeeAwareConsent)}
                            readonly={props.readonly}
                            value="Granted"
                        />
                        <CheckboxQuestion
                            questionConfig={getQuestionConfig(questionIds.DataSecureConsent)}
                            readonly={props.readonly}
                            value="Granted"
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Manager Details" id={lineManagerHrSectionId}>
                        {isCustomerUser(props.currentUser) || props.readonly ?
                            <>
                                <TextQuestion
                                    questionConfig={({ id: "LineManagerId", questionText: "Line Manager Id" })}
                                    hidden
                                    readonly
                                    answer={props.currentUser.id}
                                />
                                <TextQuestion
                                    questionConfig={getQuestionConfig(questionIds.LineManagerName)}
                                    answer={props.currentUser.name}
                                    readonly
                                />
                                <TextQuestion
                                    questionConfig={getQuestionConfig(questionIds.LineManagerEmail)}
                                    answer={props.currentUser.email}
                                    readonly
                                />
                                <TextQuestion
                                    questionConfig={getQuestionConfig(questionIds.LineManagerPhone)}
                                    answer={props.currentUser.mobilePhone}
                                    readonly
                                />
                            </>
                            : null
                        }
                        <TextQuestion
                            hideDivider
                            questionConfig={getQuestionConfig(questionIds.PurchaseOrderNumber)}
                            readonly={props.readonly}
                            mandatory={getQuestionMandatoryConfig(questionIds.PurchaseOrderNumber, false)}
                            validators={[purchaseOrderNumberValidator]}
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Employee Details" id={employeeDetailSectionId}>
                        <TextQuestion
                            hideDivider
                            questionConfig={getQuestionConfig(questionIds.ReferredTitle)}
                            readonly={props.readonly}
                            validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
                        />
                        <TextQuestion hideDivider questionConfig={getQuestionConfig(questionIds.ReferredFirstName)} readonly={props.readonly} mandatory />
                        <TextQuestion hideDivider questionConfig={getQuestionConfig(questionIds.ReferredLastName)} readonly={props.readonly} mandatory />
                        <OldDateQuestion
                            hideDivider
                            questionConfig={getQuestionConfig(questionIds.ReferredDob)}
                            readonly={props.readonly}
                            mandatory={getQuestionMandatoryConfig(questionIds.ReferredDob)}
                            validators={[ dateOfBirthInRange ]}
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.ReferredGender)}
                            options={GENDER_OPTIONS}
                            readonly={props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            hideDivider
                            questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                            readonly={props.readonly}
                            mandatory={getQuestionMandatoryConfig(questionIds.EmployeeId)}
                            validators={[ validators.validLength({ max: 20 }) ]}
                        />
                        <TextQuestion hideDivider questionConfig={getQuestionConfig(questionIds.JobTitle)} readonly={props.readonly} mandatory />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.Postcode)}
                            readonly={props.readonly}
                            validators={[createValidator(postcode => isPostcodeValid(postcode), "Postcode is not valid")]}
                            mandatory={getQuestionMandatoryConfig(questionIds.Postcode)}
                            hideDivider
                        />
                        <TextQuestion hideDivider questionConfig={getQuestionConfig(questionIds.EmailAddress)} readonly={props.readonly} mandatory />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.ConfirmEmailAddress)}
                            readonly={props.readonly}
                            validators={[createValidator(answer =>
                                answer === getAnswerToQuestionWithId(questionIds.EmailAddress), "Email addresses do not match")]}
                            validationDependencies={[getAnswerToQuestionWithId(questionIds.EmailAddress)]}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.MobilePhone)}
                            readonly={props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.HomePhone)}
                            readonly={props.readonly}
                            validators={[validators.validPhoneNumber(10)]}
                            hideDivider
                        />
                        {/* Organisational levels */}
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel1Name), id: questionIds.OrgLevel1Id })}
                            options={props.orgLevels?.filter(orgLevel => !orgLevel.parentId).map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(0, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly}
                            hideDivider
                            mandatory
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel1Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel2Name), id: questionIds.OrgLevel2Id })}
                            options={props.orgLevels && selectedOrgStructureLevelIds[0] ?
                                props.orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[0])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : []}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(1, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[0]}
                            hideDivider
                            mandatory={getQuestionMandatoryConfig(questionIds.OrgLevel2Name, false)}
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel2Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel3Name), id: questionIds.OrgLevel3Id })}
                            options={(props.orgLevels && selectedOrgStructureLevelIds[1] ?
                                props.orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[1])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : [])}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(2, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[1]}
                            hideDivider
                            mandatory={getQuestionMandatoryConfig(questionIds.OrgLevel3Name, false)}
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel3Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...getQuestionConfig(questionIds.OrgLevel4Name), id: questionIds.OrgLevel4Id })}
                            options={(props.orgLevels && selectedOrgStructureLevelIds[2] ?
                                props.orgLevels.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[2])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : [])}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => changeOrgStructureLevel(3, orgStructureId as string)}
                            search
                            readonly={props.readonly}
                            hidden={props.readonly || !selectedOrgStructureLevelIds[2]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.OrgLevel4Name)}
                            hidden={!props.readonly}
                            readonly
                            hideDivider
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Reasons for Referral" id={reasonsForReferralSectionId}>
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.AbsenceReason)}
                            multiline
                            readonly={props.readonly}
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.BodyPartAffected)}
                            filterOldOptions
                            options={BODY_PARTS}
                            readonly={props.readonly}
                            mandatory
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.CurrentWorkStatus)}
                            options={CURRENT_WORK_STATUS}
                            readonly={props.readonly}
                            mandatory
                            hideDivider
                        />
                        {getAnswerToQuestionWithId(currentWorkStatus.id) === currentWorkStatus.notAtWorkAnswer &&
                            <OldDateQuestion
                                questionConfig={getQuestionConfig(questionIds.FirstDayOfAbsence)}
                                readonly={props.readonly}
                                mandatory
                                hideDivider
                            />
                        }
                        <TextQuestion
                            questionConfig={getQuestionConfig(questionIds.AdditionalInformation)}
                            multiline
                            readonly={props.readonly}
                            hideDivider
                        />
                    </OldProgressBarQuestionnaireSection>
                    <OldProgressBarQuestionnaireSection title="Attachments" id={attachmentsSectionId} neverHide>
                        {props.formQuestions
                            .filter(question => question.answer && (question.answer as FileAnswer).blobId !== undefined)
                            .map(fileQuestion =>
                                (<FileQuestion
                                    questionConfig={({ id: fileQuestion.id, questionText: "" })}
                                    api={`${props.documentBaseApi}/${props.ohCaseId ? `${props.ohCaseId}/` : ""}document`}
                                    readonly={props.readonly}
                                    key={fileQuestion.id}
                                    onRemove={() => props.removeQuestion({ sectionId: attachmentsSectionId, id: fileQuestion.id })}
                                    acceptExtensions={acceptableExtensions}
                                />))
                        }
                        {!props.readonly &&
                            <>
                                <Button basic content="Add" onClick={addDocument} />
                                <Divider />
                            </>}
                    </OldProgressBarQuestionnaireSection>
                </ClassBasedProgressBarQuestionnaireContainer> :
                <Loader active />}
        </Container>
    );
};

const mapStateToProps = (
    state: {
        currentUser: { name: string; id: string; email: string; mobilePhone: string; roles: string[] };
        referralForm: ReferralFormState;
        questionnaires: QuestionnairesState;
        router: Location;
    }): StateProps => {
    return {
        currentUser: state.currentUser,
        formQuestions: state.questionnaires[PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY] ?
            uniq(flatten(state.questionnaires[PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY].sections.map(section => values(section.questions)))) :
            [],
        ohCaseId: state.router.params?.ohCaseId,
        customerId: state.router.params?.[CUSTOMER_ID],
        questionnaireId: state.router.params?.[PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY],
        orgLevels: state.referralForm?.orgLevels
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY })),
    openFirstSection: () => dispatch(openSection({ questionnaireKey: PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY, sectionId: "DataPrivacy" })),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY)),
    loadQuestionnaire: (apiUrl: string, customerId: string, questionnaireId: string, caseId: string) =>
        dispatch(loadQuestionnaireAction(apiUrl, customerId, questionnaireId, caseId, PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY)),
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], linkedOhCaseId: string) =>
        dispatch(submitPhysiotherapyReferralQuestionnaire(
            { formData: { questions }, linkedOhCaseId }, apiUrl, "PhysiotherapyMgrReferral", PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY))
});

export const PhysiotherapyMgrReferral = connect(mapStateToProps, mapDispatchToProps)(PhysiotherapyMgrReferralUnconnected);
